import { expandCreatureMultiattack } from '../utils/combat/CreatureAbilityResolver';
/**
 * MultiattackManager - Structured Multiattack System
 * date_of_change: 2025-09-05
 * 
 * Handles all forms of multiattack in D&D 5e:
 * - Monster Multiattack (abilities + multiple attacks) 
 * - Character Extra Attack (Fighter, etc.)
 * - Spell-granted attacks (Haste)
 * - Weapon/class feature attacks (Two-Weapon Fighting, Flurry of Blows)
 * 
 * Replaces fragile regex text parsing with structured data
 */

class MultiattackManager {
  /**
   * Resolve multiattack into component actions
   * @param {Object} action - The multiattack action
   * @param {Array} allActions - Available actions for the creature
   * @param {Array} allTraits - Available traits for the creature  
   * @param {Object} creature - The creature performing the multiattack
   * @returns {Array} Array of component actions to execute sequentially
   */
  static resolveMultiattack(action, allActions = [], allTraits = [], creature = {}) {
    if (action?.mechanics?.kind === 'multiattack') return expandCreatureMultiattack(action, allActions, creature.id);
    if (creature.contentVersion) throw new Error('Multiattack requires embedded structured mechanics and stable action IDs. See Character_Monster_Data_Architecture.md.');
    console.log(`[MultiattackManager] Resolving multiattack: ${action.name}`);
    
    // Debug: Check what we're actually getting
    console.log(`[MultiattackManager] Action has attack property:`, !!action.attack);
    if (action.attack) {
      console.log(`[MultiattackManager] Attack type:`, action.attack.type);
      console.log(`[MultiattackManager] Attack components:`, action.attack.components);
    }
    
    // 1. Check for structured multiattack data (preferred)
    if (action.multiattack) {
      console.log(`[MultiattackManager] Using structured multiattack format`);
      return this.resolveStructuredMultiattack(action.multiattack, allActions, allTraits, creature);
    }
    
    // 2. Check for new schema structured format
    if (action.attack && action.attack.type === 'multiattack' && action.attack.components) {
      console.log(`[MultiattackManager] Using components multiattack format with components:`, action.attack.components);
      return this.resolveComponentsMultiattack(action.attack.components, allActions, allTraits);
    }
    
    // 3. Check for character extra attack
    if (action.characterAttack) {
      console.log(`[MultiattackManager] Using character multiattack format`);
      return this.resolveCharacterMultiattack(action.characterAttack, allActions, creature);
    }
    
    // 4. Fallback to legacy text parsing (will be deprecated)
    console.warn(`[MultiattackManager] Falling back to legacy text parsing for: ${action.name}`);
    return this.legacyTextParsing(action, allActions, allTraits);
  }

  /**
   * Resolve structured multiattack with full component definitions
   */
  static resolveStructuredMultiattack(multiattack, allActions, allTraits, creature) {
    const components = [];
    
    for (const component of multiattack.components) {
      const count = component.count || 1;
      
      for (let i = 0; i < count; i++) {
        // Find the matching action or trait
        let matchingAction = null;
        
        if (component.type === 'ability' || component.type === 'trait') {
          matchingAction = allTraits.find(trait => 
            trait.name.toLowerCase() === component.name.toLowerCase()
          );
          if (!matchingAction) {
            matchingAction = allActions.find(action => 
              action.name.toLowerCase() === component.name.toLowerCase()
            );
          }
        } else if (component.type === 'attack') {
          matchingAction = allActions.find(action => 
            action.name.toLowerCase() === component.name.toLowerCase()
          );
        }
        
        if (matchingAction) {
          components.push({
            ...matchingAction,
            isAbility: component.type === 'ability' || component.type === 'trait',
            isAttack: component.type === 'attack',
            componentIndex: i + 1,
            componentTotal: count,
            requiresSave: !!matchingAction.save || component.type === 'ability',
            targetingRequired: component.targeting !== 'none',
            originalComponent: component
          });
          console.log(`[MultiattackManager] Added structured component: ${component.name} (${component.type})`);
        } else {
          console.warn(`[MultiattackManager] Could not find component: ${component.name}`);
        }
      }
    }
    
    return components;
  }

  /**
   * Resolve simple components array format (current Ancient Red Dragon format)
   */
  static resolveComponentsMultiattack(components, allActions, allTraits) {
    const resolvedComponents = [];
    
    for (const componentName of components) {
      // Look in actions first
      let matchingAction = allActions.find(action => 
        action.name.toLowerCase() === componentName.toLowerCase()
      );
      
      // Then look in traits
      if (!matchingAction) {
        matchingAction = allTraits.find(trait => 
          trait.name.toLowerCase() === componentName.toLowerCase()
        );
      }
      
      if (matchingAction) {
        const isAbility = this.isAbilityAction(matchingAction, componentName);
        
        resolvedComponents.push({
          ...matchingAction,
          isAbility: isAbility,
          isAttack: !isAbility,
          requiresSave: !!matchingAction.save || isAbility,
          targetingRequired: !isAbility || !!matchingAction.save // Abilities with saves need targeting
        });
        console.log(`[MultiattackManager] Added component: ${componentName} (${isAbility ? 'ability' : 'attack'})`);
      } else {
        console.warn(`[MultiattackManager] Could not find component: ${componentName}`);
      }
    }
    
    return resolvedComponents;
  }

  /**
   * Resolve character multiattack (Fighter Extra Attack, etc.)
   */
  static resolveCharacterMultiattack(characterAttack, allActions, creature) {
    const components = [];
    const baseAttack = allActions.find(action => action.name === 'Attack' || action.attackType);
    
    if (!baseAttack) {
      console.warn('[MultiattackManager] No base attack action found for character multiattack');
      return [];
    }
    
    // Add base attacks (Extra Attack feature)
    const attackCount = this.calculateAttackCount(creature, characterAttack.baseAttacks || 1);
    for (let i = 0; i < attackCount; i++) {
      components.push({
        ...baseAttack,
        name: attackCount > 1 ? `Attack ${i + 1}` : 'Attack',
        isAttack: true,
        isAbility: false,
        targetingRequired: true,
        attackNumber: i + 1,
        totalAttacks: attackCount
      });
    }
    
    // Add bonus action attacks if available
    if (characterAttack.bonusActions) {
      for (const bonusAction of characterAttack.bonusActions) {
        if (this.checkConditions(bonusAction.conditions, creature)) {
          const bonusAttackAction = allActions.find(action => 
            action.name === bonusAction.name
          ) || { ...baseAttack, name: bonusAction.name };
          
          components.push({
            ...bonusAttackAction,
            isAttack: true,
            isAbility: false,
            isBonusAction: true,
            targetingRequired: true
          });
        }
      }
    }
    
    console.log(`[MultiattackManager] Character multiattack: ${attackCount} attacks + ${characterAttack.bonusActions?.length || 0} bonus actions`);
    return components;
  }

  /**
   * Legacy fallback to text parsing (to be deprecated)
   */
  static legacyTextParsing(action, allActions, allTraits) {
    console.log(`[MultiattackManager] Legacy text parsing for: ${action.name}`);
    
    const text = action.text || action.description || action.desc || '';
    if (!text) return [];
    
    console.log(`[MultiattackManager] Parsing text: "${text}"`);
    
    const components = [];
    
    // Look for abilities mentioned in the text first (like "Frightful Presence")
    const abilityPatterns = ['frightful presence', 'terrifying howl', 'roar', 'gaze', 'aura', 'breath weapon', 'breath attack'];
    for (const abilityName of abilityPatterns) {
      if (text.toLowerCase().includes(abilityName.toLowerCase())) {
        // Find the matching ability in actions or traits
        let matchingAbility = allActions.find(a => 
          a.name && a.name.toLowerCase().includes(abilityName.toLowerCase())
        );
        if (!matchingAbility) {
          matchingAbility = allTraits.find(t => 
            t.name && t.name.toLowerCase().includes(abilityName.toLowerCase())
          );
        }
        
        if (matchingAbility) {
          components.push({
            ...matchingAbility,
            isAbility: true,
            isAttack: false,
            requiresSave: true,
            targetingRequired: true // @date_of_change 2025-09-05 - All abilities need target selection for multiattack sequences
          });
          console.log(`[MultiattackManager] Found ability component: ${matchingAbility.name}`);
        }
      }
    }
    
    // Then look for attack patterns
    const attackCounts = {};
    
    // @date_of_change 2025-09-05 - Simplified and precise pattern matching to prevent over-counting
    // Try the most specific pattern first for Ancient Red Dragon format
    const complexPattern = /makes?\s+(?:three|3)\s+attacks?:\s+(?:one|1)\s+with\s+its?\s+(\w+)\s+and\s+(?:two|2)\s+with\s+its?\s+(\w+)/gi;
    const complexMatch = complexPattern.exec(text);
    
    if (complexMatch) {
      // Handle the specific "makes three attacks: one with its bite and two with its claws" pattern
      const firstAttack = complexMatch[1].toLowerCase();
      const secondAttack = complexMatch[2].toLowerCase();
      
      const commonAttacks = ['bite', 'claw', 'claws', 'slam', 'fist', 'tail', 'wing', 'sting', 'horn', 'tusk'];
      
      if (commonAttacks.includes(firstAttack)) {
        attackCounts[firstAttack] = 1;
        console.log(`[MultiattackManager] Complex pattern: 1x ${firstAttack}`);
      }
      if (commonAttacks.includes(secondAttack)) {
        attackCounts[secondAttack] = 2; 
        console.log(`[MultiattackManager] Complex pattern: 2x ${secondAttack}`);
      }
    } else {
      // @date_of_change 2025-09-10 - CRITICAL FIX: Add pattern for "makes X attacks: one with its Y and one with its Z"
      // Handle the common D&D format: "makes two attacks: one with its beak and one with its claws"
      const listPattern = /makes?\s+(?:two|three|four|\d+)\s+attacks?:\s*(.+)/gi;
      let listMatch = listPattern.exec(text);
      if (listMatch) {
        const attacksText = listMatch[1];
        console.log(`[MultiattackManager] Found list format: "${attacksText}"`);
        
        // Extract individual attacks: "one with its beak and one with its claws"
        const individualAttackPatterns = [
          /(?:one|two|three|\d+)\s+with\s+its?\s+(\w+)/gi,
          /(?:a|an)\s+(\w+)\s+attack/gi,
          /(\w+)\s+attacks?/gi
        ];
        
        for (const pattern of individualAttackPatterns) {
          let match;
          pattern.lastIndex = 0;
          while ((match = pattern.exec(attacksText)) !== null) {
            const attackName = match[1].toLowerCase();
            const commonAttacks = ['bite', 'beak', 'claw', 'claws', 'slam', 'fist', 'tail', 'wing', 'sting', 'horn', 'tusk'];
            if (commonAttacks.includes(attackName)) {
              attackCounts[attackName] = (attackCounts[attackName] || 0) + 1;
              console.log(`[MultiattackManager] List pattern: 1x ${attackName}`);
            }
          }
        }
      }
      
      // Fallback to simpler patterns if list pattern doesn't match
      if (Object.keys(attackCounts).length === 0) {
        const simplePatterns = [
          /(?:makes?\s+)?(?:one|1|a|an)\s+(\w+)\s+attacks?/gi,
          /(?:makes?\s+)?(?:two|2)\s+(\w+)\s+attacks?/gi,
          /(?:makes?\s+)?(?:three|3)\s+(\w+)\s+attacks?/gi
        ];
        
        for (const pattern of simplePatterns) {
          let match;
          pattern.lastIndex = 0;
          while ((match = pattern.exec(text)) !== null) {
            const attackName = match[1].toLowerCase();
            const fullMatch = match[0].toLowerCase();
            
            const commonAttacks = ['bite', 'claw', 'claws', 'slam', 'fist', 'tail', 'wing', 'sting', 'horn', 'tusk'];
            if (commonAttacks.includes(attackName)) {
              let count = 1;
              if (fullMatch.includes('two') || fullMatch.includes('2')) count = 2;
              else if (fullMatch.includes('three') || fullMatch.includes('3')) count = 3;
              
              attackCounts[attackName] = (attackCounts[attackName] || 0) + count;
              console.log(`[MultiattackManager] Simple pattern: ${count}x ${attackName}`);
            }
          }
        }
      }
    }
    
    console.log(`[MultiattackManager] Parsed attack counts:`, attackCounts);
    
    // Convert attack counts to components
    for (const [attackName, count] of Object.entries(attackCounts)) {
      // @date_of_change 2025-09-05 - Enhanced attack action matching to handle singular/plural variations
      // Find the matching attack action
      const matchingAttack = allActions.find(a => {
        if (!a.name) return false;
        
        const actionName = a.name.toLowerCase();
        const parsedName = attackName.toLowerCase();
        
        return (
          actionName === parsedName ||                    // Exact match
          actionName === parsedName + 's' ||              // Add plural: claw -> claws
          actionName === parsedName.slice(0, -1) ||       // Remove plural: claws -> claw  
          actionName.includes(parsedName) ||               // Contains: "claw attack" contains "claw"
          parsedName.includes(actionName.replace(/s$/, '')) // Parsed contains action: "claws" contains "claw"
        );
      });
      
      if (matchingAttack) {
        for (let i = 0; i < count; i++) {
          components.push({
            ...matchingAttack,
            isAttack: true,
            isAbility: false,
            targetingRequired: true
          });
        }
        console.log(`[MultiattackManager] Added ${count}x ${matchingAttack.name} attacks`);
      } else {
        console.warn(`[MultiattackManager] Could not find attack action for: ${attackName}`);
      }
    }
    
    // @date_of_change 2025-09-10 - ARCHITECTURAL FIX: Use canonical data instead of fallbacks
    // Following AGENTS.md: "READ THE ACTUAL FILES" - use the originalAction canonical format
    if (components.length === 0) {
      // Use canonical attack detection - check for originalAction.attack property from monster JSON
      const canonicalAttacks = allActions.filter(a => {
        // Check if this action has canonical attack data
        const originalAction = a.originalAction || a;
        const hasCanonicalAttack = originalAction.attack && originalAction.attack.toHit !== undefined;
        const isNotMultiattack = a.name !== 'Multiattack';
        
        console.log(`[MultiattackManager] Checking ${a.name}: hasCanonicalAttack=${hasCanonicalAttack}, isNotMultiattack=${isNotMultiattack}`);
        return hasCanonicalAttack && isNotMultiattack;
      }).slice(0, 3);
      
      canonicalAttacks.forEach(attack => {
        components.push({
          ...attack,
          isAttack: true,
          isAbility: false,
          targetingRequired: true
        });
      });
      console.log(`[MultiattackManager] Using canonical attacks: ${canonicalAttacks.map(a => a.name).join(', ')}`);
    }
    
    console.log(`[MultiattackManager] Legacy parsing resolved ${components.length} components: ${components.map(c => c.name).join(', ')}`);
    return components;
  }

  /**
   * Determine if an action is an ability (requires saves) vs attack (requires attack rolls)
   */
  static isAbilityAction(action, name) {
    // Explicit save actions are abilities
    if (action.save) return true;
    
    // Known ability patterns
    const abilityPatterns = [
      'frightful presence', 'terrifying howl', 'roar', 'gaze', 'aura', 
      'breath', 'presence', 'howl', 'scream', 'stare'
    ];
    
    return abilityPatterns.some(pattern => 
      name.toLowerCase().includes(pattern) || action.name.toLowerCase().includes(pattern)
    );
  }

  /**
   * Calculate total attack count based on character level/class
   */
  static calculateAttackCount(creature, baseCount = 1) {
    if (!creature.classes) return baseCount;
    
    // Fighter Extra Attack progression
    const fighterLevel = creature.classes.find(c => c.name === 'Fighter')?.level || 0;
    if (fighterLevel >= 20) return 4;
    if (fighterLevel >= 11) return 3; 
    if (fighterLevel >= 5) return 2;
    
    // Other classes with Extra Attack at level 5
    const extraAttackClasses = ['Barbarian', 'Monk', 'Paladin', 'Ranger'];
    const hasExtraAttack = creature.classes.some(c => 
      extraAttackClasses.includes(c.name) && c.level >= 5
    );
    
    return hasExtraAttack ? 2 : baseCount;
  }

  /**
   * Check if conditions are met for bonus actions/abilities
   */
  static checkConditions(conditions, creature) {
    if (!conditions) return true;
    
    for (const condition of conditions) {
      switch (condition) {
        case 'two_weapon_fighting':
          // Check if creature has light weapons equipped
          // Simplified check - would need equipment data
          return true; // Placeholder
        case 'flurry_of_blows':
          // Check if creature has ki points and is monk
          return creature.classes?.some(c => c.name === 'Monk') || false;
        default:
          console.warn(`[MultiattackManager] Unknown condition: ${condition}`);
          return false;
      }
    }
    
    return true;
  }

  /**
   * Validate that all multiattack components can be executed
   */
  static validateMultiattack(components, creature) {
    const errors = [];
    
    for (const component of components) {
      // Check if component requires resources that aren't available
      if (component.originalComponent?.resources) {
        // Check spell slots, ki points, etc.
        // Placeholder for resource validation
      }
      
      // Check if recharge abilities are available
      if (component.recharge) {
        // Placeholder for recharge validation
      }
    }
    
    return { valid: errors.length === 0, errors };
  }

  /**
   * Create structured multiattack data for legacy actions
   */
  static createStructuredMultiattack(name, components) {
    return {
      name: name,
      type: 'multiattack',
      multiattack: {
        components: components.map(comp => ({
          name: comp.name,
          type: comp.isAbility ? 'ability' : 'attack',
          count: comp.count || 1,
          targeting: comp.requiresSave ? 'area' : 'individual'
        })),
        execution: 'sequential',
        targeting: 'individual'
      }
    };
  }
}

export default MultiattackManager;

// Example usage for different multiattack types:

/* 
// 1. Monster Multiattack (structured)
const ancientRedDragonMultiattack = {
  name: "Multiattack",
  type: "multiattack",
  multiattack: {
    components: [
      { name: "Frightful Presence", type: "ability", targeting: "area" },
      { name: "Bite", type: "attack", count: 1 },
      { name: "Claw", type: "attack", count: 2 }
    ],
    execution: "sequential",
    targeting: "individual"
  }
};

// 2. Character Multiattack (Fighter Extra Attack)
const fighterAttack = {
  name: "Attack", 
  type: "attack",
  characterAttack: {
    baseAttacks: 1, // Will be calculated based on level
    bonusActions: [
      { name: "Off-hand Attack", type: "attack", conditions: ["two_weapon_fighting"] }
    ]
  }
};

// 3. Spell-granted Multiattack (Haste)
const hasteAttack = {
  name: "Hasted Attack",
  type: "attack",
  source: "spell",
  spellName: "Haste", 
  characterAttack: {
    baseAttacks: 1,
    restrictions: ["weapon_attack_only"]
  }
};

// 4. Class Feature Multiattack (Monk Flurry of Blows)
const flurryOfBlows = {
  name: "Flurry of Blows",
  type: "multiattack", 
  multiattack: {
    components: [
      { name: "Unarmed Strike", type: "attack", count: 2 }
    ],
    execution: "sequential",
    cost: { type: "ki_points", amount: 1 }
  }
};
*/